/**
 * native ops 依頼（Firestore `ops_requests`、No ≥ 10000）を BQ `service_operation.native_ops_requests`
 * へ **append-on-write**（各 ops 書き込み時に INSERT）で写す行スキーマ（単一 source）。infra はこの記述子
 * から Pulumi の table schema を組み立て、ミラー行への写像（`mapOpsDocToBqRow`）もここに置いて product-api と
 * customer-success MCP が共有する（列定義も写像も二重に持たない）。
 *
 * design.md §2.4 のとおり **バッチ / mirror 同期ジョブは持たない**。各書き込みが発生時に INSERT-only で
 * append され、`service_operation.unified_requests` VIEW 側が
 * `QUALIFY ROW_NUMBER() OVER (PARTITION BY id ORDER BY updated_at DESC) = 1` で latest-row-wins に畳む
 * （単一行 MERGE より安く、維持すべき mirror が無い）。この table は SLO（VIEW への UNION で native も
 * 集計対象に入る）と AI ポイント見積もり（estimator が VIEW 経由で native の close+未 point を拾う）の
 * 両方の基盤になる。VIEW 側の表示用文字列（request_date / points 等）は VIEW の SELECT で typed 列から
 * 導出するため、この table は **typed な素の値**（TIMESTAMP・INT64 + 業務ロジックを含む `status`）だけを持つ。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business native ops 依頼を BQ へ append-on-write で写す native_ops_requests table の列定義・ドキュメント→append 行の写像・streaming insert 行への変換（単一 source）。infra の table schema と product-api / customer-success MCP の append がこの記述子と写像を共有する
 * @graph-connects none
 */
import { nativeOpsDerivedFields } from './requests.js';
/**
 * ops ミラー・VIEW を置く BigQuery データセット（`unified_requests` / `native_ops_requests` / `raw_*` /
 * `point_estimates`）。product-api・customer-success MCP・point estimator が同じ名前を参照する単一 source。
 * @graph-connects none
 */
export const OPS_BQ_DATASET = 'service_operation';
/**
 * native ops 依頼（Firestore 発番）の append-on-write ミラーのテーブル。append（product-api / MCP の
 * link_ops_bug_ticket）とステート履歴の読み取りが同じ名前を参照する単一 source。
 * @graph-connects none
 */
export const NATIVE_OPS_BQ_TABLE = 'native_ops_requests';
/**
 * `native_ops_requests` の列定義（順序含めた単一 source）。infra は `JSON.stringify` して Pulumi の
 * `gcp.bigquery.Table.schema` に、product-api は append 行の組み立て（`NativeOpsRequestRow`）に使う。
 * append-on-write なので id は unique ではなく（1 依頼が更新のたびに複数行 append される）、VIEW 側が
 * `id` で latest-row-wins に畳む dedup キーになる。
 * @graph-connects none
 */
export const NATIVE_OPS_BQ_SCHEMA = [
    {
        name: 'id',
        type: 'STRING',
        mode: 'REQUIRED',
        description: 'ops id（slug:No）。VIEW の dedup キー',
    },
    {
        name: 'service_name',
        type: 'STRING',
        mode: 'REQUIRED',
        description: 'BQ 表示サービス名（unified_requests の JOIN キー。SERVICE_SLUG_TO_NAME）',
    },
    {
        name: 'row_number',
        type: 'INTEGER',
        mode: 'REQUIRED',
        description: 'unified_requests の JOIN キー。native は依頼 No（≥10000、sheet 行番号と非衝突）',
    },
    { name: 'request_no', type: 'STRING', mode: 'REQUIRED', description: '依頼 No（display No）' },
    {
        name: 'state',
        type: 'STRING',
        mode: 'REQUIRED',
        description: '進捗ステート（生値。OpsState）',
    },
    {
        name: 'status',
        type: 'STRING',
        mode: 'REQUIRED',
        description: 'VIEW / estimator 用の status フリーテキスト（opsBqStatus = classifyState の逆写像）',
    },
    { name: 'request_category', type: 'STRING', mode: 'NULLABLE', description: '依頼カテゴリ' },
    { name: 'urgency', type: 'STRING', mode: 'NULLABLE', description: '緊急度' },
    { name: 'assignee', type: 'STRING', mode: 'NULLABLE', description: '当番オペ担 nickName' },
    { name: 'requester', type: 'STRING', mode: 'NULLABLE', description: '起票者 nickName' },
    { name: 'background', type: 'STRING', mode: 'NULLABLE', description: '背景（5W）' },
    { name: 'request_content', type: 'STRING', mode: 'NULLABLE', description: '対応してほしい内容' },
    {
        name: 'classification',
        type: 'STRING',
        mode: 'NULLABLE',
        description: '調査後分類（OpsClassification）',
    },
    { name: 'point', type: 'INTEGER', mode: 'NULLABLE', description: '工数ポイント（0–21 / null）' },
    {
        name: 'point_source',
        type: 'STRING',
        mode: 'NULLABLE',
        description: 'point の設定元（manual / null）',
    },
    { name: 'submitted_at', type: 'TIMESTAMP', mode: 'REQUIRED', description: '起票日時' },
    {
        name: 'completed_at',
        type: 'TIMESTAMP',
        mode: 'NULLABLE',
        description: '解決日時（未完了は null）',
    },
    {
        name: 'updated_at',
        type: 'TIMESTAMP',
        mode: 'REQUIRED',
        description: '最終更新日時（VIEW の latest-row-wins 並べ替えキー）',
    },
    {
        name: 'appended_at',
        type: 'TIMESTAMP',
        mode: 'REQUIRED',
        description: 'この行を BQ へ append した時刻（updated_at 同値時の tiebreak / デバッグ用）',
    },
    // 末尾に置くのは意図的。BigQuery の列追加は既存列の後ろにしか入らず、実テーブルの列順と
    // この定数の並びを一致させておかないと Pulumi の schema 差分が「追加」ではなく並べ替えに見える。
    // 新しい列は必ず末尾へ足すこと。
    //
    // 型が STRING なのは legacy シート由来の `work_minutes`（"300" 等の分数文字列）に合わせるため。
    // unified_requests VIEW は legacy arm と native arm を UNION するので、INTEGER にすると型不一致で
    // VIEW 全体が壊れる。
    {
        name: 'work_minutes',
        type: 'STRING',
        mode: 'NULLABLE',
        description: '作業時間（分）。legacy シート互換の文字列。未記録は null',
    },
    // 差戻し操作で append した行にだけ入る（それ以外の書き込みの行は null）。state='blocked' の行は担当変更等
    // でも append されるため、差戻しの件数・理由はこの列が非 null の行で数える。
    {
        name: 'send_back_reason',
        type: 'STRING',
        mode: 'NULLABLE',
        description: '差戻し理由（OpsSendBackReason: missing_info / unclear / cannot_confirm / need_wfid）。差戻し操作の行のみ非 null',
    },
    // 補足対応として起票した依頼の行にだけ入る（補足対応の件数・元依頼ごとの集計に使う）。
    {
        name: 'follow_up_of',
        type: 'STRING',
        mode: 'NULLABLE',
        description: '補足対応元の依頼 id（slug:No）。補足対応として起票した依頼の行のみ非 null',
    },
];
/** TIMESTAMP 列名（streaming insert 時に BigQuery timestamp へ包む対象）。 @graph-connects none */
export const NATIVE_OPS_BQ_TIMESTAMP_COLUMNS = NATIVE_OPS_BQ_SCHEMA.filter((f) => f.type === 'TIMESTAMP').map((f) => f.name);
/**
 * native ops 依頼ドキュメントを BQ append 行へ写像する。service_name / row_number / request_no / status / point は
 * `nativeOpsDerivedFields` を使い、**MCP の Firestore 直読（`nativeOpsDocToRequestRow`）と同じ計算**にする
 * （同じ依頼が経路によって違う分類・違う JOIN キーで見えるのを防ぐ）。
 *
 * product-api と customer-success MCP（`link_ops_bug_ticket`）の両方が append するため ops-core に置く。
 * 写像が 2 つあると、片方だけ列を足したときに VIEW の latest-row-wins で「どちらの経路が最後に書いたか」で
 * 列の値が揺れる。
 * @graph-connects nativeOpsDerivedFields [calls] service_name / row_number / request_no / status / point の共有計算
 */
export function mapOpsDocToBqRow(doc, appendedAt, sendBackReason) {
    const { serviceName, rowNumber, requestNo, status, point } = nativeOpsDerivedFields(doc);
    // row_number は REQUIRED INT64 かつ VIEW の JOIN キー。append 経路は native id（No ≥ 10000）に絞る
    // 前提だが、legacy/非数値 id を直接渡されると NaN が入り INSERT が壊れる。不変条件として明示的に弾く
    // （共有計算は throw しないので、書き込み側の要件はここで担保する）。
    if (!Number.isInteger(rowNumber)) {
        throw new Error(`native ops append: non-numeric ops No '${doc.id}' (row_number must be INT64)`);
    }
    return {
        id: doc.id,
        service_name: serviceName,
        row_number: rowNumber,
        request_no: requestNo,
        state: doc.state,
        status,
        request_category: doc.category,
        urgency: doc.urgency,
        assignee: doc.assigneeName,
        requester: doc.requesterName,
        background: doc.background,
        request_content: doc.requested,
        // classification は create 時 null 固定で、以後は**関連チケットの射影**として更新される
        // （`deriveOpsClassification`: 「本件調査結果バグ」が紐付いていれば bug）。これにより status の
        // `バグチケット` 分岐（`opsBqStatus`）へ native 依頼も到達し、バグ転換率が実体を持つ。
        // 分岐が効くのは state='closed' のときだけなので、調査中に紐付けた分は close 時の append で確定する。
        classification: doc.classification,
        point,
        point_source: doc.pointSource ?? null,
        submitted_at: doc.submittedAt,
        completed_at: doc.completedAt,
        updated_at: doc.updatedAt,
        appended_at: appendedAt,
        // legacy シートの work_minutes と同じ STRING 列へ入れる（VIEW が両 arm を UNION するため）。
        // 未記録は null。0 は「作業していない」ではなく「未記録」と区別できないので保存側で弾く。
        work_minutes: doc.workMinutes == null ? null : String(doc.workMinutes),
        // 差戻し操作の行だけキーを付ける。null を明示せず省略するのは、列追加（core stack）より先に
        // product-api がデプロイされた窓で、差戻し以外の全行まで未知列で insert 失敗させないため。
        ...(sendBackReason !== undefined ? { send_back_reason: sendBackReason } : {}),
        // 補足対応の依頼だけキーを付ける（省略の理由は send_back_reason と同じ）。
        ...(doc.followUpOf !== undefined ? { follow_up_of: doc.followUpOf } : {}),
    };
}
/**
 * append 行を streaming insert 用の行に変換する（TIMESTAMP 列だけ `toTimestamp` で包み、空は null）。
 *
 * TIMESTAMP は ISO 文字列を素で渡すと insertAll の書式判定に揺れが出るため、各アプリの BigQuery client の
 * `timestamp()` で包む。client の型は ops-core に持ち込まず（依存を増やさない）、包む関数だけを受け取る。
 * @graph-connects none
 */
export function toNativeOpsInsertRow(row, toTimestamp) {
    const insertRow = { ...row };
    for (const col of NATIVE_OPS_BQ_TIMESTAMP_COLUMNS) {
        const value = row[col];
        insertRow[col] = typeof value === 'string' && value !== '' ? toTimestamp(value) : null;
    }
    return insertRow;
}
//# sourceMappingURL=ops-bq.js.map